<template>
  <div class="checkout-form__main checkout-form__same-day-order">

    <div v-if="hasError()" class="checkout-form__form-error js-checkout-errors">
      Please correct the following errors:
      <ul>
        <li v-for="(message, field) in errors">
          <a :href="fieldErrorLink(field)">{{ message }}</a>
        </li>
      </ul>
    </div>

    <!--
    <div v-if="!customerInfo.is_logged_in" class="checkout-form__login">
      <div class="checkout-form__login-heading">
        <h2>Already have an account?</h2>
      </div>
      <div class="checkout-form__login-message">
        <p>If you are a returning customer, <a :href="loginUrl">log in now</a> to bring up your saved info.</p>
      </div>
      <div class="checkout-form__login-cta">
        <a :href="loginUrl">Log In</a>
      </div>
    </div>
    -->

    <div class="checkout-form__form">

      <div v-show="collectionMethod === null" class="checkout-form__collection-method">
        <div class="checkout-form__collection-method-message">
          <p>This order is for...</p>
        </div>
        <div class="checkout-form__collection-method-choices">
          <div class="checkout-form__collection-method-choice checkout-form__collection-method-choice--pickup">
            <button type="button" @click="setCollectionMethod('pickup')" class="checkout-form__collection-method-choice-button" :class="{ 'checkout-form__collection-method-choice-button--active': !flyboyPickupIsDelivery }" :disabled="!collectionMethodAvailable('pickup')" :title="collectionMethodAvailable('pickup') ? '' : 'We are currently unable to take same day orders for pickup'">Pickup</button>
          </div>
          <div class="checkout-form__collection-method-choice checkout-form__collection-method-choice--delivery">
            <button type="button" @click="setCollectionMethod('delivery')" class="checkout-form__collection-method-choice-button" :class="{ 'checkout-form__collection-method-choice-button--active': flyboyPickupIsDelivery }" :disabled="!collectionMethodAvailable('delivery')" :title="collectionMethodAvailable('delivery') ? '' : 'We are currently unable to take same day orders for delivery'">Delivery</button>
            <div class="checkout-form__collection-method-choice-note">$15 minimum order</div>
          </div>
        </div>
      </div>

      <form v-show="collectionMethod !== null" novalidate method="post" :action="checkoutAction" @submit.prevent="handleFormSubmit">

        <!-- TODO disable pickup or delivery if no time slots available? -->
        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">
            {{ flyboyPickupIsDelivery ? 'Delivery Info' : 'Pickup Info' }}
            <div v-if="numCollectionMethodsAvailable > 1" class="checkout-form__collection-method-toggle">
              <a href="#switch" @click.prevent="toggleCollectionMethod">Switch to {{ flyboyPickupIsDelivery ? 'pickup' : 'delivery' }}</a>
            </div>
            <img v-show="isLocationInfoUpdating" :src="loadingIndicatorSrc" width="16" height="16" alt="">
          </legend>

          <div v-if="flyboyPickupIsDelivery" id="flyboy_location_id" class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_location_id') }">
            <div class="checkout-form__input-field-locations">
              <label v-for="location in flyboyLocationOptionsOriginalDelivery">
                <img :src="location.photo_src" alt="">
                <div class="checkout-form__input-field-locations-title-info">
                  <div class="checkout-form__input-field-locations-title">
                    {{ location.title }}
                  </div>
                  <div v-if="location.additional_info_same_day" class="checkout-form__input-field-locations-info" v-html="location.additional_info_same_day"></div>
                </div>
              </label>
            </div>
          </div>
          <div v-else>
             <div class="checkout-form__input-error">
              {{ getError('flyboy_location_id') }}
            </div>
            <div class="checkout-form__input-field-locations">
              <!-- TODO disable locations for which there are no times available today? -->
              <label v-for="location in flyboyLocationOptionsNotOriginalDelivery" :class="{'this-location-not-available': !locationAvailableToday(location)}">
                <img :src="location.photo_src" alt="">
                <input type="radio" name="flyboy_location_id" :value="location.id" v-model="flyboyPickupInfo.locationId" :disabled="!locationAvailableToday(location)">
                <div class="checkout-form__input-field-locations-title-info">
                  <div class="checkout-form__input-field-locations-title">
                    {{ location.title }}
                  </div>
                  <div v-if="location.additional_info_same_day" class="checkout-form__input-field-locations-info" v-html="location.additional_info_same_day"></div>
                </div>
              </label>
            </div>
          </div>

          <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_pickup_at') }">
            <div class="checkout-form__input-error">
              {{ getError('flyboy_pickup_at') }}
            </div>
            <div class="checkout-form__input-label">
              <label for="flyboyPickupTime">{{ flyboyPickupIsDelivery ? 'Deliver between' : 'Have ready by' }} *</label>
            </div>
            <div class="checkout-form__input-field">
              <div class="checkout-form__input-note" v-show="!(flyboyPickupInfo.locationId && flyboyPickupInfo.date)">
                NOTE: choose a location to see available times.
              </div>
              <select id="flyboyPickupTime" name="flyboyPickupTime" v-model="flyboyPickupInfo.time">
                <option :value="null">--Choose--</option>
                <option v-for="(value, key) in flyboyPickupTimeOptions" :value="key">{{ value + " on " + monthAndDay }}</option>
              </select>
            </div>
          </div>

          <div v-show="flyboyPickupIsDelivery">
            <div class="checkout-form__input-group">
              <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_deliver_contact') }">
                <div class="checkout-form__input-error">
                  {{ getError('flyboy_deliver_contact') }}
                </div>
                <div class="checkout-form__input-label">
                  <label for="flyboy_deliver_contact">Delivery Contact *</label>
                </div>
                <div class="checkout-form__input-field">
                  <input type="text" name="flyboy_deliver_contact" id="flyboy_deliver_contact" v-model.trim="flyboyPickupInfo.deliverContact" maxlength="255">
                </div>
              </div>
              <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_deliver_phone') }">
                <div class="checkout-form__input-error">
                  {{ getError('flyboy_deliver_phone') }}
                </div>
                <div class="checkout-form__input-label">
                  <label for="flyboy_deliver_phone">Delivery Contact Phone *</label>
                </div>
                <div class="checkout-form__input-field">
                  <input type="tel" name="flyboy_deliver_phone" id="flyboy_deliver_phone" v-model.trim="flyboyPickupInfo.deliverPhone" maxlength="25" @input="dashify('flyboyPickupInfo', 'deliverPhone')">
                </div>
              </div>
            </div>
            <div class="checkout-form__input-note" style="margin:-12px 0 13px;">
              If at business please list name of person AND name of business
            </div>
            <div class="checkout-form__input-group">
              <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_deliver_to') }">
                <div class="checkout-form__input-error">
                  {{ getError('flyboy_deliver_to') }}
                </div>
                <div class="checkout-form__input-label">
                  <label for="flyboy_deliver_to">Deliver To Location/Address *</label>
                </div>
                <div class="checkout-form__input-field">
                  <input type="text" ref="flyboy_deliver_to" name="flyboy_deliver_to" id="flyboy_deliver_to" @keyup="addressManuallyChanged" v-model="flyboyPickupInfo.deliverTo">
                  <div>
                    <template v-if="calculatingDeliveryFee || cartSummary.delivery_amt > 0">
                      Delivery fee:
                        <img v-if="calculatingDeliveryFee" :src="loadingIndicatorSrc" width="16" height="16" alt="">
                        <template v-else>{{ priceFormat(cartSummary.delivery_amt) }}</template>
                    </template>
                  </div>
                </div>
              </div>
            </div>
            <div class="checkout-form__input-group">
              <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_deliver_message') }">
                <div class="checkout-form__input-error">
                  {{ getError('flyboy_deliver_message') }}
                </div>
                <div class="checkout-form__input-label">
                  <label for="flyboy_deliver_message">Message To Include With Delivery</label>
                </div>
                <div class="checkout-form__input-field">
                  <textarea name="flyboy_deliver_message" id="flyboy_deliver_message" v-model="flyboyPickupInfo.deliverMessage"></textarea>
                </div>
              </div>
              <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('flyboy_deliver_instructions') }">
                <div class="checkout-form__input-error">
                  {{ getError('flyboy_deliver_instructions') }}
                </div>
                <div class="checkout-form__input-label">
                  <label for="flyboy_deliver_instructions">Any Special Delivery Instructions?</label>
                </div>
                <div class="checkout-form__input-field">
                  <textarea name="flyboy_deliver_instructions" id="flyboy_deliver_instructions" v-model="flyboyPickupInfo.deliverInstructions"></textarea>
                </div>
              </div>
            </div>
          </div>

        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Choose the quantity of any items you would like...</legend>
          <div class="shopping-cart-same-day">
            <div class="shopping-cart-same-day__items">
              <div v-for="product in products" class="shopping-cart-same-day__item">
                <div class="shopping-cart-same-day__item-image">
                  <img :src="product.primary_photo_src" :alt="product.primary_photo_alt">
                </div>
                <div class="shopping-cart-same-day__item-title">
                  <h3>{{ product.title }}</h3>
                </div>
                <div v-if="product.options" class="shopping-cart-same-day__item-options">
<!-- TODO options support???
                  <?php $addon_product_option = reset($addon_product->options); ?>
                  <?php $addon_product_option_value = ($item && array_key_exists($addon_product_option->id, $item->options)) ? $item->options[$addon_product_option->id]->value : null; ?>
                  <label for="flyboy-addon-product-<?=$addon_product->id?>-instructions">Notes:</label>
                  <input type="text" id="flyboy-addon-product-<?=$addon_product->id?>-instructions" name="flyboy_addon_products[<?=$addon_product->id?>][options][<?=$addon_product_option->id?>]" value="<?=h($addon_product_option_value)?>" placeholder="Notes">
-->
                </div>
                <div class="shopping-cart-same-day__item-price"><span v-html="product.price_html"></span> ea.</div>
                <div class="shopping-cart-same-day__item-quantity">
                  <button type="button" @click="quantityDecrement(product.id)">-</button>
                  <input type="number" min="0" v-model="productQuantities[product.id]">
                  <button type="button" @click="quantityIncrement(product.id)">+</button>
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Coupon/Promo Code</legend>
            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('coupon_code') }">
              <div class="checkout-form__input-field">
                <input type="text" name="coupon_code" id="coupon_code" v-model.trim="flyboyPickupInfo.couponCode" maxlength="255" @keypress="couponCodeKeypress">
              </div>
              <div class="checkout-form__coupon-submit">
                <button type="button" @click.prevent="applyCoupon">Apply</button>
              </div>
              <div class="checkout-form__input-error">
                {{ getError('coupon_code') }}
              </div>
            </div>
          </legend>
        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Tip</legend>
          
          <div class="checkout-form__input-field-tips">
              <div class="checkout-form__input-field-tips-input">
                  <div class="checkout-form__input-field-tips-input-percents">
                    <button type="button" @click="setTipToPercent(10)">10%</button>
                    <button type="button" @click="setTipToPercent(15)">15%</button>
                    <button type="button" @click="setTipToPercent(20)">20%</button>
                  </div>
                  <div class="checkout-form__input-field-tips-input-label-and-input">
                    <div class="checkout-form__input-field-tips-input-label">
                      <label for="tipAmount">Amount</label>
                    </div>
                    <div class="checkout-form__input-field-tips-input-field">
                      <input type="number" min="0" max="100" name="tipAmount" id="tipAmount" v-model.number="tipAmountDisplay" class="currency">
                    </div>
                  </div>
              </div>
              <div class="checkout-form__input-field-tips-image">
                <img src="/images/tips.jpg" alt="FYI: tips are split between our overnight bakers as a bonus for their skilled hard work" />
              </div>
          </div>
          
        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Cart Summary <img v-show="isLocationInfoUpdating" :src="loadingIndicatorSrc" width="16" height="16" alt=""></legend>

          <div class="checkout-form__summary">
            <div class="checkout-form__summary-liner">

              <div class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  Subtotal
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(subtotal) }}
                </div>
              </div>

              <div v-if="flyboyPickupIsDelivery" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                 Delivery Fee
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(cartSummary.delivery_amt) }}
                </div>
              </div>

              <div v-if="cartSummary.surcharge_amt" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  {{ cartSummary.surcharge_desc }}
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(cartSummary.surcharge_amt) }}
                </div>
              </div>

              <div v-if="surcharge_pct_amt > 0" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  {{ cartSummary.surcharge_pct_desc }}
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(surcharge_pct_amt) }}
                </div>
              </div>

              <div v-if="haveCoupon" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  Discount {{ discount_message ? '('+discount_message+')' : '' }}
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(discount_amt, '', '-$') }}
                </div>
              </div>

              <div v-if="tax_amt" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  Sales Tax ({{ cartSummary.tax_pct }}%)
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(tax_amt) }}
                </div>
              </div>

              <div v-if="tipAmount" class="checkout-form__summary-price">
                <div class="checkout-form__summary-price-title">
                  Tip
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(tipAmount) }}
                </div>
              </div>

              <div class="checkout-form__summary-price checkout-form__summary-price--add-spacer checkout-form__summary-price--large">
                <div class="checkout-form__summary-price-title">
                  Grand Total
                </div>
                <div class="checkout-form__summary-price-value">
                  {{ priceFormat(grand_total_incl_tip) }}
                </div>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Comments</legend>
          <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_comments') }">
            <div class="checkout-form__input-error">
              {{ getError('customer_comments') }}
            </div>
            <div class="checkout-form__input-label">
              <label for="customer_comments">Any comments or additional information regarding your Express Order?</label>
            </div>
            <div class="checkout-form__input-note">
              * Please note that if you want specific donuts included in your dozens you may comment here, but we can NOT guarantee these will be available depending on supply.
            </div>
            <div class="checkout-form__input-field">
              <textarea name="customer_comments" id="customer_comments" v-model="flyboyPickupInfo.customer_comments"></textarea>
            </div>
          </div>
        </fieldset>

        <fieldset class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Your Contact Info</legend>

          <div class="checkout-form__input-group">
            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_first_name') }">
              <div class="checkout-form__input-error">
                {{ getError('customer_first_name') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="customer_first_name">First Name *</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="text" name="customer_first_name" id="customer_first_name" v-model.trim="customerInfo.customer_first_name" maxlength="255">
              </div>
            </div>

            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_last_name') }">
              <div class="checkout-form__input-error">
                {{ getError('customer_last_name') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="customer_last_name">Last Name *</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="text" name="customer_last_name" id="customer_last_name" v-model.trim="customerInfo.customer_last_name" maxlength="255">
              </div>
            </div>
          </div>

          <div class="checkout-form__input-group">
            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_email') }">
              <div class="checkout-form__input-error">
                {{ getError('customer_email') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="customer_email">Email Address *</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="email" name="customer_email" id="customer_email" v-model.trim="customerInfo.customer_email" maxlength="255">
              </div>
            </div>

            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_phone') }">
              <div class="checkout-form__input-error">
                {{ getError('customer_phone') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="customer_phone">Phone # *</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="tel" name="customer_phone" id="customer_phone" v-model.trim="customerInfo.customer_phone" maxlength="25" @input="dashify('customerInfo', 'customer_phone')">
              </div>
            </div>

            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('customer_zip') }">
              <div class="checkout-form__input-error">
                {{ getError('customer_zip') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="customer_zip">Zip *</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="text" name="customer_zip" id="customer_zip" v-model.trim="customerInfo.customer_zip" maxlength="25">
              </div>
            </div>
          </div>

          <div class="checkout-form__input-group">
            <div class="checkout-form__input">
              <span class="checkout-form__input-field">
                <input type="checkbox" name="mailOptin" id="mailOptin" value="1" v-model="mailOptin">
              </span>
              &nbsp;<span class="checkout-form__input-label">
                <label for="customer_optin">Sign up to receive sale notifications & occasional marketing emails about new specials (no more than 2 emails per week).</label>
              </span>
            </div>
          </div>
        </fieldset>

        <fieldset id="payment-fieldset" class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Payment Information <img v-show="isLocationInfoUpdating" :src="loadingIndicatorSrc" width="16" height="16" alt=""></legend>

          <div :class="{ 'checkout-form__input-group-error': hasError('stripe') }">
            {{ getError('stripe') }}
          </div>

          <div v-show="productQuantitiesTotal < 1" class="checkout-form__input">
            Please select the items you'd like to order above.
          </div>

          <div v-show="grand_total > 0 && isOfflinePaymentAllowed" class="checkout-form__input">
            <div class="checkout-form__input-field">
              <label>
                <input type="radio" name="is_offline_payment" v-model="isOfflinePayment" :value="false">
                Pay online (now)
              </label>
              <label>
                <input type="radio" name="is_offline_payment" v-model="isOfflinePayment" :value="true">
                Pay in store (when I pick up the order)
              </label>
            </div>
          </div>

          <div v-show="grand_total > 0 && !(isOfflinePaymentAllowed && isOfflinePayment)">
            <div class="checkout-form__input-group">
              <div id="stripe_payment" style="width: 100%"></div>
            </div>
          </div>
        </fieldset>

        <fieldset v-if="!customerInfo.is_logged_in" class="checkout-form__fieldset">
          <legend class="checkout-form__legend">Registration (Optional)</legend>

          <div class="checkout-form__fieldset-note" v-show="!accountExists">
            Registering allows you to log in to the website and review your order history. It also makes the checkout process quicker in the future since your contact info can be automatically filled in.
          </div>

          <div class="checkout-form__input" v-show="!accountExists && !accountShowRegistrationFields">
            <div class="checkout-form__input-field">
              <label>
                <input type="checkbox" name="register" v-model="customerInfo.register">
                Register a new account
              </label>
            </div>
          </div>

          <div v-show="accountCheckingEmail">
            Checking email address...
          </div>

          <div v-show="!accountCheckingEmail && accountExists">
            We found an account with your email address in our records. We will associate this order with your account.
          </div>

          <div v-show="!accountCheckingEmail && accountShowRegistrationFields">
            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('password') }">
              <div class="checkout-form__input-error">
                {{ getError('password') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="password">Enter a password</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="password" name="password" id="password" v-model="customerInfo.password">
              </div>
            </div>

            <div class="checkout-form__input" :class="{ 'checkout-form__input--has-error': hasError('password_confirm') }">
              <div class="checkout-form__input-error">
                {{ getError('password_confirm') }}
              </div>
              <div class="checkout-form__input-label">
                <label for="password_confirm">Re-Enter password</label>
              </div>
              <div class="checkout-form__input-field">
                <input type="password" name="password_confirm" id="password_confirm" v-model="customerInfo.password_confirm">
              </div>
            </div>
          </div>
        </fieldset>

        <div class="checkout-form__submit">
          <div class="checkout-form__submit-button checkout-form__submit-button--ready">
            <button v-show="!isProcessing" :disabled="isProcessing">Complete Your Order</button>
            <span v-show="isProcessing">
              <img :src="loadingIndicatorSrc" width="16" height="16" alt="">
              Processing...
            </span>
          </div>
        </div>

      </form>
    </div>

  </div>
</template>

<script>
  import moment from 'moment'
  export default {
    name: 'same-day-order-form-app',

    mounted() {
      //$('#flyboy_pickup_at').attr('autocomplete','off'); // disable autocomplete because it conflicts with the datepicker. Also, it doesn't make sense to autocomplete this field anyway.

      //set up google maps autocomplete for the deliver-to address field
      this.autocomplete = new google.maps.places.Autocomplete(
        (this.$refs.flyboy_deliver_to),
        {
          //bias towards Sioux Falls
          bounds: new google.maps.LatLngBounds(
            new google.maps.LatLng(43.4630853, -96.8406987),
            new google.maps.LatLng(43.5897517, -96.6333145),
          ),
          strictBounds: false,
          types: ['address'],
        },
      )
      this.autocomplete.addListener('place_changed', () => {
        //this doesn't get updated automatically when user selects an autocomplete option
        this.flyboyPickupInfo.deliverTo = this.autocomplete.getPlace().formatted_address

        //calculate distances to all flyboy locations and base delivery fee off of closest to destination
        if (this.directionsService === null) {
          this.directionsService = new google.maps.DirectionsService()
        }
        this.calculatingDeliveryFee = true
        let deliveryDistanceInMeters = 0
        let nearestFlyboyLocationId = 0
        let distancesToCheck = this.flyboyLocationOptionsDelivery.length
        this.flyboyLocationOptionsDelivery.forEach((o) => {
          if (o.address) {
            var request = {
              origin: this.flyboyPickupInfo.deliverTo,
              destination: o.address,
              travelMode: 'DRIVING',
            }
            this.directionsService.route(request, function(result, status) {
              if (status === 'OK') {
                let distanceInMeters = result.routes[0].legs.reduce((accumulator, leg) => accumulator + leg.distance.value, 0)
                if (deliveryDistanceInMeters === 0 || distanceInMeters < deliveryDistanceInMeters) {
                  nearestFlyboyLocationId = o.id
                  deliveryDistanceInMeters = distanceInMeters
                }
              }
              if (--distancesToCheck < 1) {
                let deliveryDistanceInMiles = deliveryDistanceInMeters / 1609.344
                if (deliveryDistanceInMeters < 1) {
                  this.flyboyPickupInfo.deliveryOriginLocationId = null
                  this.flyboyPickupInfo.deliveryOption = null
                  alert('Unable to determine your location, please enter a new address.')
                } else if (deliveryDistanceInMiles > 5) {
                  this.flyboyPickupInfo.deliveryOriginLocationId = null
                  this.flyboyPickupInfo.deliveryOption = null
                  alert('Delivery location outside of our delivery area. Please enter another address or reload the page and choose pick-up.')
                } else {

                  this.flyboyPickupInfo.deliveryOriginLocationId = nearestFlyboyLocationId

                  if (deliveryDistanceInMiles <= 5) {
                    this.flyboyPickupInfo.deliveryOption = 1
                  } else if (deliveryDistanceInMiles <= 10) {
                    this.flyboyPickupInfo.deliveryOption = 2
                  } else {
                    this.flyboyPickupInfo.deliveryOption = 3
                  }
                }
                this.calculatingDeliveryFee = false
              }
            }.bind(this))
          }
        })
      })
    },

    data() {
      let productQuantitiesObject = {}
      checkoutAppData.products.forEach(product => { productQuantitiesObject[product.id] = 0 })
      return {
        stripe: {
          clientSecret: '',
          elements: null,
        },
        accountCheckingEmail: false,
        accountExists: false,
        accountShowRegistrationFields: false,
        //`checkoutAppData` is set by PHP when the page is first served (via inline script tag at the bottom of the checkout.php single_page)
        calculatingDeliveryFee: false,
        cartSummary: {},
        couponResult: {},
        customerInfo: checkoutAppData.customerInfo,
        mailOptin: false,
        collectionMethod: null,
        directionsService: null,
        errors: {}, //will contain any validation errors returned by the server after we submit the form data
        isLocationInfoUpdating: false, //UI flag, becomes true while cart summary is being looked up after a location change
        isProcessing: false, //UI flag, becomes true while the form is processing (after the submit button is clicked)
        flyboyPickupInfo: {
          locationId: null,
          date: checkoutAppData.sameDayDate,
          time: null,
          deliverContact: null,
          deliverPhone: null,
          deliverTo: null,
          deliverMessage: null,
          deliverInstructions: null,
          deliveryOption: null,
          deliveryOriginLocationId: null,
        },
        isForTomorrow: checkoutAppData.isForTomorrow,
        isOfflinePayment: false,
        tipAmount: 0,
        tipPercentage: 0,
        productQuantities: productQuantitiesObject,
      }
    },

    created() {

      //this.initPayment()

      //this data will never change, so we don't need it to be "reactive"
      // (hence we don't need to put it into `data()` above)
      this.sameDayOrdersEnabled = {
        delivery: checkoutAppData.enable_same_day_orders_delivery,
        pickup: checkoutAppData.enable_same_day_orders_pickup,
      }
      this.products = checkoutAppData.products
      this.applyCouponUrl = checkoutAppData.applyCouponUrl
      this.checkEmailUrl = checkoutAppData.checkEmailUrl
      this.checkoutAction = checkoutAppData.checkoutAction
      this.validateAction = checkoutAppData.validateAction
      this.updateLocationInfoUrl = checkoutAppData.updateLocationInfoUrl
      this.loginUrl = checkoutAppData.loginUrl
      this.csrfToken = checkoutAppData.csrfToken
      this.countryOptions = checkoutAppData.countryOptions
      this.flyboyLocationOptions = checkoutAppData.flyboyLocationOptions.filter(o => o.allow_same_day_orders == 1)
      this.loadingIndicatorSrc = CCM_IMAGE_PATH + '/throbber_white_16.gif' //CCM_IMAGE_PATH is defined by C5 on every page
      this.requireOnlinePayMinAmt = checkoutAppData.requireOnlinePayMinAmt

      //this.moment = require('moment')

      this.updateLocationInfo()
      if (checkoutAppData.collectionMethod) {
        this.setCollectionMethod(checkoutAppData.collectionMethod)
      }

      //if only one method is available, just set it to that
      if (this.collectionMethodAvailable('pickup')) {
        if (!this.collectionMethodAvailable('delivery')) {
          this.setCollectionMethod('pickup')
        }
      } else {
        if (this.collectionMethodAvailable('delivery')) {
          this.setCollectionMethod('delivery')
        }
      }
    },

    computed: {

      monthAndDay() {
        return moment(this.flyboyPickupInfo.date).format('MMM Do')
      },

      numCollectionMethodsAvailable() {
        let retval = 0
        if (this.collectionMethodAvailable('delivery')) {
          retval++
        }
        if (this.collectionMethodAvailable('pickup')) {
          retval++
        }
        return retval
      },

      isOfflinePaymentAllowed() {
        if (this.grand_total < this.requireOnlinePayMinAmt) {
          for (let o of this.flyboyLocationOptions) {
            if (o.id == this.flyboyPickupInfo.locationId) {
              return o.allow_offline_payments == '1'
            }
          }
        }
      },

      //Note these are not inverses because the is_delivery flag was hijacked from meaning "this is
      // the delivery location" to "this location supports delivery". They are used in different
      // ways, flyboyLocationOptionsDelivery is used to get the list of locations that support
      // delivery and flyboyLocationOptionsNonDelivery is used to get all locations that are not
      // the original Delivery location, i.e., locations that support order pickup
      flyboyLocationOptionsDelivery() {
        return this.flyboyLocationOptions.filter(function(o) { return o.is_delivery == 1 })
      },
      flyboyLocationOptionsNonDelivery() {
        return this.flyboyLocationOptions.filter(function(o) { return o.is_delivery != 1 })
      },
      flyboyLocationOptionsOriginalDelivery() {
        return this.flyboyLocationOptions.filter(function(o) { return o.id == 3 })
      },
      flyboyLocationOptionsNotOriginalDelivery() {
        return this.flyboyLocationOptions.filter(function(o) { return o.id != 3 })
      },

      flyboyPickupDateOptions() {
        return this.flyboyPickupDateOptionObjects.map(o => o.date)
      },

      flyboyPickupDateOptionObjects() {
        if (this.flyboyPickupInfo.locationId) {
          for (let o of this.flyboyLocationOptions) {
            if (o.id == this.flyboyPickupInfo.locationId) {
              return o.pickup_options
            }
          }
        }

        return []
      },

      flyboyPickupTimeOptions() {
        if (this.flyboyPickupInfo.date) {
          for (const o of this.flyboyPickupDateOptionObjects) {
            if (o.date == moment(this.flyboyPickupInfo.date).format('YYYY-MM-DD')) {
              let retval = {}
              if (this.isForTomorrow) {
                retval = o.times
              } else {
                const currentDate = new Date();
                currentDate.setTime(currentDate.getTime() + 20*60*1000) //add twenty minutes, 15 + 5 extra to give them time to fill out the form
                const currentTime = ("0" + currentDate.getHours()).slice(-2) + ':' + ("0" + (currentDate.getMinutes())).slice(-2); //ensure leading zeros
                for (const prop in o.times) {
                  if (prop > currentTime) {
                    retval[prop] = o.times[prop]
                  }
                }
              }
              if (this.flyboyPickupIsDelivery) {
                for (const prop in retval) {
                  if (prop == '06:00') {
                    delete retval[prop]
                  }
                }
              }
              return retval
            }
          }
        }

        return []
      },

      flyboyDeliveryOptionsObjects() {
        if (this.flyboyPickupInfo.locationId) {
          for (let o of this.flyboyLocationOptions) {
            if (o.id == this.flyboyPickupInfo.locationId) {
              return o.delivery_options
            }
          }
        }

        return []
      },

      flyboyPickupIsDelivery() {
        return this.collectionMethod === "delivery"
      },

      flyboyPickupOpenDate() {
        return moment().add(1, 'days').toDate()
      },

      productQuantitiesTotal() {
        return this.products
          ? this.products.reduce((accumulator, product) => accumulator + this.productQuantities[product.id], 0)
          : 0
      },
      subtotal() {
        return this.products
          ? this.roundTo(this.products.reduce((accumulator, product) => accumulator + this.productQuantities[product.id] * product.price, 0), 2)
          : 0
      },
      haveCoupon() {
        return typeof this.couponResult.is_valid != 'undefined'
      },
      couponDiscountMessage() {
        let retval = { discount: 0, message: '' }
        if (this.haveCoupon) {
          const subtotal_plus_delivery_plus_surcharge = this.subtotal + this.cartSummary.delivery_amt + this.cartSummary.surcharge_amt
          if (!this.couponResult.is_valid) {
            retval.message = this.couponResult.message ? this.couponResult.message : 'Invalid coupon/promo code'
          } else if (subtotal_plus_delivery_plus_surcharge < this.couponResult.min_subtotal) {
            retval.message = this.priceFormat(this.couponResult.min_subtotal) + ' minimum purchase required'
          } else {
            switch (this.couponResult.discount_type) {
              case 'amt':
                retval.discount = Math.min(this.couponResult.discount_amt, subtotal_plus_delivery_plus_surcharge)
                retval.message = 'Up to ' + this.priceFormat(this.couponResult.discount_amt) + ' off'
                break;
              case 'pct':
                retval.discount = this.couponResult.discount_pct * subtotal_plus_delivery_plus_surcharge
                retval.message = '' + Math.round(this.couponResult.discount_pct * 100) + '% off'
                break;
              case 'shp':
                retval.discount = this.cartSummary.delivery_amt
                retval.message = 'Free shipping'
                break;
              default:
                retval.message = 'Error applying coupon'
            }
          }
        }
        return retval
      },
      discount_amt() {
        return this.couponDiscountMessage.discount
      },
      discount_message() {
        return this.couponDiscountMessage.message
      },
      surcharge_pct_amt() {
        return this.roundTo(
          this.cartSummary.surcharge_pct * (
            this.subtotal
            + this.cartSummary.delivery_amt
            + this.cartSummary.surcharge_amt
            - this.discount_amt
          ),
          2
        )
      },
      tax_amt() {
        return this.roundTo(((this.subtotal + this.cartSummary.delivery_amt + this.cartSummary.surcharge_amt + this.surcharge_pct_amt - this.discount_amt) * this.cartSummary.tax_pct) / 100, 2)
      },
      grand_total() {
        //console.log(this.subtotal, this.cartSummary.delivery_amt, this.cartSummary.surcharge_amt, this.surcharge_pct_amt, this.discount_amt, this.tax_amt)
        return this.roundTo(this.subtotal + this.cartSummary.delivery_amt + this.cartSummary.surcharge_amt + this.surcharge_pct_amt - this.discount_amt + this.tax_amt, 2)
      },
      grand_total_incl_tip() {
        return this.roundTo(this.grand_total + parseFloat(this.tipAmount > 0 ? this.tipAmount : 0), 2)
      },

      tipAmountDisplay: {
        get: function() { return parseFloat(this.tipAmount).toFixed(2) },
        set: function(newValue) { this.tipAmount = newValue > 0 ? newValue : 0 },
      },
    },

    methods: {

      roundTo(n, digits) { // Thx Rick Calder https://stackoverflow.com/a/15762794
        var negative = false
        if (digits === undefined) {
            digits = 0
        }
        if (n < 0) {
          negative = true
          n = n * -1
        }
        var multiplicator = Math.pow(10, digits)
        n = parseFloat((n * multiplicator).toFixed(11))
        n = (Math.round(n) / multiplicator).toFixed(digits)
        if (negative) {
          n = (n * -1).toFixed(digits)
        }
        return parseFloat(n)
      },

      //Stripe
      async generatePaymentIntent() {
        const amount = this.grand_total_incl_tip
        //console.log(amount)
        if (isNaN(amount) || amount <= 0) {
        //console.log('clearing clientSecret')
          this.stripe.clientSecret = ''
        } else {
          //console.log('retrieving clientSecret...')
          const that = this
          const { clientSecret } = await fetch(checkoutAppData.stripeToolsUrl, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ amount: amount }),
          }).then((r) => r.json());
          //console.log(clientSecret)
          this.stripe.clientSecret = clientSecret;
        }
      },
      async initPayment() {
        if (this.productQuantitiesTotal > 0 && this.grand_total_incl_tip > 0) {
          await this.generatePaymentIntent();
          const clientSecret = this.stripe.clientSecret
          if (clientSecret != '') {
            this.stripe.elements = stripe.elements({ clientSecret });
            const paymentElementOptions = {
              layout: "tabs",
            };
            const paymentElement = this.stripe.elements.create("payment", paymentElementOptions);
            paymentElement.mount("#stripe_payment");
          }
        }
      },
      pay() {
        //console.log('Paying, return_url:', window.location.href)
        const that = this
        stripe.confirmPayment({
          elements: this.stripe.elements,
          confirmParams: {
            return_url: window.location.href,
          },
        })
        .then(function(result) {
          that.isProcessing = false
          //console.log('Pay result:', result)
          if (result.error) {
            that.errors.stripe = result.error.message

            //scroll up to the payment section
            that.$nextTick(() => { //wait until dom is updated (to ensure the errors div actually exists)
              that.isProcessing = false
              $('html, body').animate({
                scrollTop: $('#payment-fieldset').offset().top
              }, 200)
            })
          }
        });
      },

      addressManuallyChanged() {
        //address manually changed => invalidate delivery origin and fee so order cannot be submitted until the address is autocompleted
        this.flyboyPickupInfo.deliveryOriginLocationId = null
        this.flyboyPickupInfo.deliveryOption = null
      },

      //add dashes to phone numbers
      //a => data array name
      //b => array element name
      dashify(a, b) {
        const digits = this[a][b].replace(/[^\d]+/g, '')
        let dashified = ''
        if (digits.length > 0) dashified += digits.substr(0, 3)
        if (digits.length > 3) dashified += '-'+digits.substr(3, 3)
        if (digits.length > 6) dashified += '-'+digits.substr(6, 4)
        this[a][b] = dashified
      },

      locationAvailableToday(location) {
        return location.pickup_options.length > 0
      },

      quantityDecrement(productId) {
        if (this.productQuantities[productId] > 0) {
          this.productQuantities[productId]--
        }
      },
      quantityIncrement(productId) {
        this.productQuantities[productId]++
      },

      collectionMethodAvailable(method) {
        return this.sameDayOrdersEnabled[method] == '1'
      },
      setCollectionMethod(method) {
        if (['delivery', 'pickup'].indexOf(method) >= 0 && this.collectionMethodAvailable(method)) {
          this.collectionMethod = method
          this.flyboyPickupInfo.locationId = method === 'delivery' ? 3 : null
        } else {
          alert(`We are currently unable to take same day orders for ${method}.`)
        }
      },
      toggleCollectionMethod(event) {
        this.setCollectionMethod(this.collectionMethod === 'delivery' ? 'pickup' : 'delivery')
        event.target.blur()
      },

      flyboyIsDisabledDate(date) {
        let dateToTest = moment(date).format('YYYY-MM-DD')
        if (this.flyboyPickupDateOptions.indexOf(dateToTest) != -1) {
          return false
        }
        return true
      },

      hasError(key = null) {
        if (key) {
          return this.errors.hasOwnProperty(key)
        } else {
          return !!Object.keys(this.errors).length
        }
      },
      getError(key) {
        return this.hasError(key) ? this.errors[key] : null
      },
      clearError(key) {
        if (this.hasError(key)) {
          delete(this.errors[key])
        }
      },
      fieldErrorLink(field) {
        if (field == 'order' || field == 'items') {
          field = '' //these errors are not tied to a specific field
        }
        return '#' + field
      },

      priceFormat(n, returnIfNaN = ' - ', prependSymbol = '$') {
        n = parseFloat(n)
        if (isNaN(n)) {
          return returnIfNaN
        } else {
          return prependSymbol + n.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,') //http://stackoverflow.com/a/14428340/477513
        }
      },

      updateLocationInfo() {
        this.isLocationInfoUpdating = true
        let data = {
          'location_id': this.flyboyPickupInfo.locationId,
          'delivery_option_id': this.flyboyPickupInfo.deliveryOption,
        }
        $.post(this.updateLocationInfoUrl, {
          'ccm_token': this.csrfToken,
          'data': JSON.stringify(data), //data is sent as JSON instead of POST vars, because nulls and falses were getting interpreted as strings in PHP when sent as POST vars (some issue with jQuery 1.7 maybe?)
        }, cartSummary => {
          this.cartSummary = cartSummary
          this.initPayment()
          this.isLocationInfoUpdating = false
        }, 'json')
      },

      couponCodeKeypress($event) {
        if ($event.key === 'Enter') {
          $event.preventDefault()
          this.applyCoupon()
        }
      },
      applyCoupon() {
        this.clearError('coupon_code')
        this.couponResult = {}
        if (this.flyboyPickupInfo.couponCode) {
          this.isLocationInfoUpdating = true
          let data = {
            'coupon_code': this.flyboyPickupInfo.couponCode,
          }
          $.post(this.applyCouponUrl, {
            'ccm_token': this.csrfToken,
            'data': JSON.stringify(data), //data is sent as JSON instead of POST vars, because nulls and falses were getting interpreted as strings in PHP when sent as POST vars (some issue with jQuery 1.7 maybe?)
          }, couponResult => {
            this.couponResult = couponResult
            this.isLocationInfoUpdating = false
            if (!couponResult.is_valid) {
	            this.errors['coupon_code'] = couponResult.message ? couponResult.message : 'Invalid coupon/promo code'
            }
          }, 'json')
        }
      },

      setTipToPercent(pct) {
        this.tipPercentage = pct
        this.tipAmount = this.roundTo((this.subtotal*pct)/100, 2)
      },

      handleFormSubmit() {
        this.isProcessing = true

        let data = Object.assign({}, this.customerInfo, {
          'product_quantities': this.productQuantities,
          'flyboy_location_id': this.flyboyPickupInfo.locationId,
          'flyboy_pickup_at': this.flyboyPickupInfo.date && this.flyboyPickupInfo.time
            ? (this.flyboyPickupInfo.time == 'ASAP'
              ? 'ASAP'
              : moment(this.flyboyPickupInfo.date).format('YYYY-MM-DD') + ' ' + this.flyboyPickupInfo.time)
            : null,
          'is_offline_payment': (this.isOfflinePaymentAllowed && this.isOfflinePayment),
          'is_delivery': this.flyboyPickupIsDelivery,
          'flyboy_deliver_contact': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliverContact : null),
          'flyboy_deliver_phone': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliverPhone : null),
          'flyboy_deliver_to': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliverTo : null),
          'flyboy_deliver_instructions': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliverInstructions : null),
          'flyboy_deliver_message': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliverMessage : null),
          'flyboy_delivery_option': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliveryOption : null),
          'flyboy_delivery_origin_location_id': (this.flyboyPickupIsDelivery ? this.flyboyPickupInfo.deliveryOriginLocationId : null),
          'customer_comments': this.flyboyPickupInfo.customer_comments,
          'coupon_code': this.flyboyPickupInfo.couponCode,
          'mail_optin': this.mailOptin,
          'tip_amt': this.tipAmount,
        })

        if (this.grand_total <= 0) {
          //no charge
          this.checkout(data)

        } else if (this.flyboyPickupIsDelivery && this.subtotal < 15) {
          this.errors = ['Minimum delivery order is $15']
          this.scrollToErrors()

        } else if (this.cartSummary.is_offline_payment_allowed && this.isOfflinePayment) {
          //customer chose to pay offline (in the store), so skip credit card processing
          this.checkout(data)

        } else {
          //order requires payment - validate inputs then pay with stripe
          $.post(this.validateAction, {
            ccm_token: this.csrfToken,
            data: JSON.stringify(data), //data is sent as JSON instead of POST vars, because nulls and falses were getting interpreted as strings in PHP when sent as POST vars
            flyboyPickupInfo: this.flyboyPickupInfo,
          }, response => {
            //console.log('Validation response:', response)
            if (response.success) {
              this.pay()
            } else {
              this.errors = response.errors

              // It's possible the error is because they selected offline payment, went back to cart (in another window/tab) and added more items to the cart (making payment required), and tried to checkout. So, update the cart summary to trigger UI changes.
              this.updateLocationInfo()

              //scroll up to the error messages
              this.$nextTick(() => { //wait until dom is updated (to ensure the errors div actually exists)
                this.isProcessing = false
                $('html, body').animate({
                  scrollTop: $('.js-checkout-errors').offset().top
                }, 200)
              })
            }
          }, 'json')
        }
      },

      checkout(data) {
        if (typeof Sentry !== 'undefined') Sentry.captureMessage(data)
        $.post(this.checkoutAction, {
          ccm_token: this.csrfToken,
          data: JSON.stringify(data), //data is sent as JSON instead of POST vars, because nulls and falses were getting interpreted as strings in PHP when sent as POST vars
          flyboyPickupInfo: this.flyboyPickupInfo,
        }, response => {
          //console.log('checkout response:', response)
          if (response.success) {
            window.location.href = response.redirect
          } else {
            this.errors = response.errors

            // It's possible the error is because they selected offline payment, went back to cart (in another window/tab) and added more items to the cart (making payment required), and tried to checkout. So, update the cart summary to trigger UI changes.
            this.updateLocationInfo()

            this.scrollToErrors()
          }
        }, 'json')
      },

      scrollToErrors() {
        this.$nextTick(() => { //wait until dom is updated (to ensure the errors div actually exists)
          this.isProcessing = false
          $('html, body').animate({
            scrollTop: $('.js-checkout-errors').offset().top - 5
          }, 200)
        })
      }
    },

    watch: {
      // seems like this should work but it doesn't so we have to watch individual changes
      //grand_total_incl_tip: function() {
      //  this.initPayment()
      //},
      'cartSummary.delivery_amt': function() {
        this.initPayment()
      },
      discount_amt: function(newVal, oldVal) {
        this.initPayment()
      },
      productQuantities: {
        deep: true,
        handler: function() {
          this.initPayment()
        },
      },
      tipAmount: function() {
        this.initPayment()
      },
      'customerInfo.register': function(val) {
        if (val) {
          if (!this.customerInfo.customer_email || this.customerInfo.customer_email.trim().length < 1) {
            alert('Please enter an email address first.')
            this.$nextTick(() => this.customerInfo.register = false)
          } else {
            this.accountCheckingEmail = true
            $.get(
              this.checkEmailUrl,
              { email: this.customerInfo.customer_email.trim() },
              result => {
                if (result) {
                  this.accountExists = true
                  this.accountShowRegistrationFields = false
                  this.customerInfo.register = false
                } else {
                  this.accountExists = false
                  this.accountShowRegistrationFields = true
                }
                this.accountCheckingEmail = false
              },
              'json'
            )
          }
        }
      },
      'flyboyPickupInfo.locationId': function(val) {
        this.updateLocationInfo()
      },
      'flyboyPickupInfo.deliveryOption': function(val) {
        this.updateLocationInfo()
      },
      'cartSummary.is_offline_payment_allowed': function(val) {
        if (val != true) {
          this.isOfflinePayment = false
        }
      }
    }
  }
</script>



// WEBPACK FOOTER //
// App.vue?46f2b36a